import { observable, action } from 'mobx';
import { FormikHelpers } from 'formik';

import {
  API_FUNCTION_TYPE,
  API_ACCOUNT_FEES_REGULAR,
  API_ACCOUNT_FEES_REGULAR_MODEL,
  API_ACCOUNT_DETAIL_FEES_REGULAR,
  API_ACCOUNT_DETAIL_FEES_REGULAR_MODEL,
  API_ACCOUNT_FEES_REGULAR_USETEMPLATE,
  API_ACCOUNT_FEES_REGULAR_SAVETEMPLATE,
  API_ACCOUNT_FEES_SPLIT,
  API_ACCOUNT_FEES_LIMIT,
  API_ACCOUNT_DETAIL_FEES_CREATE,
  API_ACCOUNT_DETAIL_FEES_REGULAR_USETEMPLATE,
  API_ACCOUNT_DETAIL_FEES_REGULAR_SAVETEMPLATE,
} from 'api/endpoints';
import { ACCOUNTS_EDIT_CONTRACT } from 'pages/Accounts/Edit/accountEditRoutes';
import { SingleValueResponse } from 'common/interfaces';

import history from 'helpers/routerHistory';
import { FormStore } from '../formStore';
import { RootStore } from 'stores/rootStore';
import { RegularFeesData, FeeLine, FeesLimits } from 'pages/Accounts/Edit/Fees/Fees';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { DetailPage, Flows } from 'pages/Accounts/Detail/enums';
import { Currency, AccountMode } from 'enums';
import { f2p } from 'helpers/helpers';

export class WorkflowRegularFeesStore extends FormStore<any> {
  @observable tab = 0;
  accountId = '';
  originalData: RegularFeesData | null = null;
  endpoint: API_FUNCTION_TYPE;
  endpointModel: API_FUNCTION_TYPE;
  endpointUseTemplate: API_FUNCTION_TYPE;
  endpointSaveTemplate: API_FUNCTION_TYPE;
  viewMode?: ViewMode;
  accountMode: AccountMode;
  @observable draft = false;
  @observable modelOk = true;
  @observable feesLimits: FeesLimits = {};

  constructor(rootStore: RootStore, accountId: string, accountMode: AccountMode, viewMode?: ViewMode) {
    super(rootStore);
    this.accountId = accountId;
    this.endpoint = viewMode === ViewMode.DETAIL ? API_ACCOUNT_DETAIL_FEES_REGULAR : API_ACCOUNT_FEES_REGULAR;
    this.endpointModel =
      viewMode === ViewMode.DETAIL ? API_ACCOUNT_DETAIL_FEES_REGULAR_MODEL : API_ACCOUNT_FEES_REGULAR_MODEL;
    this.endpointUseTemplate =
      viewMode === ViewMode.DETAIL ? API_ACCOUNT_DETAIL_FEES_REGULAR_USETEMPLATE : API_ACCOUNT_FEES_REGULAR_USETEMPLATE;
    this.endpointSaveTemplate =
      viewMode === ViewMode.DETAIL
        ? API_ACCOUNT_DETAIL_FEES_REGULAR_SAVETEMPLATE
        : API_ACCOUNT_FEES_REGULAR_SAVETEMPLATE;
    this.viewMode = viewMode;
    this.accountMode = accountMode;
  }

  @action
  setDraft = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.patch<SingleValueResponse<boolean>>(
      API_ACCOUNT_DETAIL_FEES_CREATE(this.accountId),
    );
    if (response?.data?.value) {
      this.draft = true;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  fetchFees = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<RegularFeesData>(this.endpoint(this.accountId));
    if (response?.data) {
      this.originalData = JSON.parse(JSON.stringify(response.data));
      if (response.data.performance) response.data.performance.total = f2p(response.data.performance.total);
      response.data.service = response.data.service.map((s) => ({ ...s, total: f2p(s.total) }));
      if (response.data.serviceLow) response.data.serviceLow.total = f2p(response.data.serviceLow.total);
      this.data = response.data;
    }

    this.draft = !!this.data?.draftExists;
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  useTemplate = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<RegularFeesData>(
      this.endpointUseTemplate(this.accountId),
    );
    if (response?.data) {
      if (response.data.performance) response.data.performance.total = f2p(response.data.performance.total);
      response.data.service = response.data.service.map((s) => ({ ...s, total: f2p(s.total) }));
      if (response.data.serviceLow) response.data.serviceLow.total = f2p(response.data.serviceLow.total);
      this.data = response.data;
      this.handleSubmit(this.data);
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  handleSubmit = async (data: RegularFeesData, template?: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    if (data.draftExists) delete data.draftExists;
    if (data.templateExists) delete data.templateExists;
    data.performance.total /= 100;
    data.service = data.service.map((s) => ({ ...s, total: s.total / 100 }));
    data.serviceLow.total /= 100;
    const submitted = await this.submitData(
      `${template ? this.endpointSaveTemplate(this.accountId) : this.endpoint(this.accountId)}`,
      data,
      {
        singleValueResponse: true,
        reset: false,
        validationPrefixDepth: 3,
      },
    );

    if (submitted && !template) {
      await this.fetchFees();
    } else {
      data.performance.total = f2p(this.data.performance.total);
      data.service = data.service.map((s) => ({ ...s, total: f2p(s.total) }));
      data.serviceLow.total = f2p(this.data.serviceLow.total);
      this.data.templateExists = true;
    }

    this.rootStore.stores.uiStore.setLoading(false);
    return submitted;
  };

  handleRefresh = async (data: RegularFeesData, formikHelpers: FormikHelpers<RegularFeesData>) => {
    data.performance.total /= 100;
    data.service = data.service.map((s) => ({ ...s, total: s.total / 100 }));
    data.serviceLow.total /= 100;
    this.modelOk = await this.submitData(`${this.endpointModel(this.accountId)}`, data);
    if (this.modelOk) {
      this.data.performance.total = f2p(this.data.performance.total);
      this.data.service = this.data.service.map((s: FeeLine) => ({ ...s, total: f2p(s.total) }));
      this.data.serviceLow.total = f2p(this.data.serviceLow.total);
      await formikHelpers.setValues(this.data);
    } else {
      await formikHelpers.setFieldValue('performance.total', f2p(data.performance.total));
      data.service.forEach(async (s, i) => await formikHelpers.setFieldValue(`service[${i}].total`, f2p(s.total)));
      await formikHelpers.setFieldValue('serviceLow.total', f2p(data.serviceLow.total));
    }
    return this.modelOk;
  };

  checkFeeSavings = async (data: RegularFeesData) => {
    const input = JSON.parse(JSON.stringify(data)) as RegularFeesData;
    input.performance.total /= 100;
    input.service = data.service.map((s) => ({ ...s, total: s.total / 100 }));
    input.serviceLow.total /= 100;
    const response = await this.rootStore.stores.apiStore.post<RegularFeesData>(this.endpointModel(this.accountId), input);
    if (response?.data) this.data.izFeeSavingsRuleApplied = response.data.izFeeSavingsRuleApplied;
  };

  restoreOriginalData = (formikHelpers: FormikHelpers<RegularFeesData>) => {
    const tempData = JSON.parse(JSON.stringify(this.originalData));
    tempData.performance.total = f2p(tempData.performance.total);
    tempData.service = tempData.service.map((s: FeeLine) => ({ ...s, total: f2p(s.total) }));
    formikHelpers.setValues(tempData as RegularFeesData);
    this.data = tempData;
  };

  checkLimits = async (currency: Currency, performance: number) => {
    const response = await this.rootStore.stores.apiStore.post<FeesLimits>(API_ACCOUNT_FEES_LIMIT, {
      performance: performance / 100,
      currency: currency,
      mode: this.accountMode,
    });
    if (response?.data) {
      this.feesLimits = response.data;
    }
  };

  recalculateSplit = async (
    name: string,
    fee: FeeLine,
    formikHelpers: FormikHelpers<RegularFeesData>,
    currency: Currency,
    performance: number,
    bracket: boolean,
    validate: boolean,
  ) => {
    if (validate) await this.checkLimits(currency, performance);
    const newValues = await this.rootStore.stores.apiStore.post<FeeLine>(API_ACCOUNT_FEES_SPLIT, {
      key: bracket ? fee.title : undefined,
      total: fee.total / 100,
      performance: performance / 100,
      ocp: bracket ? undefined : fee.ocp,
      currency: currency,
      mode: this.accountMode,
    });
    newValues?.data && formikHelpers.setFieldValue(name, { ...newValues.data, total: f2p(newValues.data.total) });
  };

  @action handleNext = async (
    data: RegularFeesData,
    formikHelpers: FormikHelpers<RegularFeesData>,
    flow?: Flows,
    goFwd?: () => void,
  ) => {
    const submitted = flow === Flows.PLAN && !this.draft ? true : await this.handleSubmit(data);
    if (submitted) {
      if (goFwd) {
        goFwd();
      } else {
        history.push(ACCOUNTS_EDIT_CONTRACT(this.accountId));
      }
    }
  };

  @action handleBack = (tabChange?: Function, goBack?: () => void) => {
    if (goBack) {
      goBack();
    } else if (tabChange) {
      tabChange(DetailPage.SINGLE_FEES);
    }
  };
}
